import { PreliminaryPrice } from '@api/api/price-schema';
import { SnakeMeta } from '@api/api/timeseries-schema';
import { isWeekend, isWithinInterval, parse } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

import {
  Formats,
  getTimeMinusOffset,
  LocalisedDateFormatting,
} from '../dates/dates';
import { PriceStatus } from '../numbers/numbers';

export const EST_TIME_ZONE = 'America/New_York';
export const SUPPORTED_EXCHANGES = ['nyse', 'nasd', 'arca', 'bats'] as const;

export interface PriceChangeParams {
  lastPrice: number;
  newPrice: number;
  decimals?: number;
  isPrice?: boolean;
}

export interface PriceChange {
  status: PriceStatus;
  change: string;
  proportionChange: string;
}

export interface DisplayPriceChange {
  proportionChange: string;
}

export interface MarketHours {
  isPreMarket: boolean;
  isAfterMarket: boolean;
  isMarketHours: boolean;
}

export interface OutsideHoursText {
  label: string;
  tag: 'pre' | 'after';
}

export interface GetOutsideHoursTextProps {
  marketHours: Pick<MarketHours, 'isPreMarket' | 'isAfterMarket'>;
  preMarketLabel: string;
  afterHoursLabel: string;
}

export interface PriceBase {
  price?: number;
  priceChange?: PriceChange;
  livePrice?: number;
  isLive?: boolean;
  marketHours: MarketHours;
  isSupported: boolean;
  asOfDate?: string;
}

export interface SecondaryPrice extends PriceBase {
  outsideHours?: OutsideHoursText;
}

export interface FormatPriceProps {
  base: PriceBase;
  decimals: number;
  meta?: SnakeMeta;
  preliminaryPrice?: PreliminaryPrice;
  dateLocale?: string;
  preMarketLabel: string;
  afterHoursLabel: string;
}

export interface FormatPreliminaryDateProps {
  preliminaryTime: string;
  dateLocale?: string;
}

export interface SnakeSeriesItem {
  value?: number | null;
  index?: string;
  time?: string;
  close?: number;
}

export interface MapSnakeSeriesToOhlcProps<TTime> {
  data: SnakeSeriesItem[];
  isPrice: boolean;
  parseTime?: (time: string) => TTime;
}

export interface OhlcPoint<TTime> {
  time: TTime;
  open: number;
  high: number;
  low: number;
  close: number;
}

export const getTimeFromSeries = (isPrice: boolean, time: string) =>
  isPrice ? time : `${time.slice(0, 10)}T00:00:00Z`;

export const mapSnakeSeriesToOhlc = <TTime = string>({
  data,
  isPrice,
  parseTime = time => time as TTime,
}: MapSnakeSeriesToOhlcProps<TTime>): OhlcPoint<TTime>[] =>
  data
    .filter(item => (item.value ?? item.close) != null)
    .map(item => {
      const time = getTimeFromSeries(
        isPrice,
        (item.index ?? item.time) as string
      );
      const close = (item.value ?? item.close) as number;

      return {
        time: parseTime(time),
        open: 0,
        high: 0,
        low: 0,
        close,
      };
    });

const getCommonChangeParams = (
  lastPrice: number,
  newPrice: number,
  decimals: number
) => {
  let change = (0).toFixed(decimals);
  let status = PriceStatus.Default;
  const absoluteChange = newPrice - lastPrice;

  if (!lastPrice) {
    return { sign: '', change, status, isChanged: false };
  }

  const sign = Math.sign(absoluteChange) >= 0 ? '+' : '-';
  const isChanged = absoluteChange !== 0;

  if (isChanged) {
    change = `${sign}${Math.abs(absoluteChange).toFixed(decimals)}`;
    status = sign === '+' ? PriceStatus.Positive : PriceStatus.Negative;
  }

  return { sign, change, status, isChanged };
};

export const getPriceChange = ({
  lastPrice,
  newPrice,
  decimals = 2,
  isPrice = true,
}: PriceChangeParams): { priceChange: PriceChange } => {
  const suffix = isPrice ? '%' : 'bps';
  let proportionChange = `0.00${suffix}`;
  const { sign, status, change, isChanged } = getCommonChangeParams(
    lastPrice,
    newPrice,
    decimals
  );

  if (isChanged) {
    const percentageValue = isPrice
      ? (newPrice / lastPrice - 1) * 100
      : (newPrice - lastPrice) * 100;

    proportionChange = `${sign}${Math.abs(percentageValue).toFixed(
      2
    )}${suffix}`;
  }

  return {
    priceChange: {
      status,
      change,
      proportionChange,
    },
  };
};

const formatPreliminary = ({
  preliminaryPrice,
  decimals,
  lastPrice,
  price,
  dateLocale,
}: {
  preliminaryPrice: PreliminaryPrice;
  decimals: number;
  lastPrice: number;
  price: number;
  dateLocale?: string;
}) => ({
  price,
  priceChange: getPriceChange({
    lastPrice,
    newPrice: price,
    decimals,
  }).priceChange,
  asOfDate: formatPreliminaryDate({
    preliminaryTime: preliminaryPrice.time,
    dateLocale,
  }),
});

const formatOfficialClose = (decimals: number, meta?: SnakeMeta) => ({
  price: meta?.last_value,
  priceChange: getPriceChange({
    lastPrice: meta?.before_last_value ?? 0,
    newPrice: meta?.last_value ?? 0,
    decimals,
  }).priceChange,
});

export const formatPrice = ({
  base,
  meta,
  preliminaryPrice,
  decimals,
  dateLocale,
  preMarketLabel,
  afterHoursLabel,
}: FormatPriceProps): {
  primaryPrice: PriceBase;
  secondaryPrice: SecondaryPrice;
} => {
  const isLive = base.marketHours.isMarketHours && base.isLive;
  const isPreliminary =
    !!meta?.alified_last_timestamp &&
    !!preliminaryPrice &&
    checkIsPreliminary(meta.alified_last_timestamp, preliminaryPrice.time);

  const officialCloseDetails = !isLive
    ? formatOfficialClose(decimals, meta)
    : base;
  const customPrimaryDetails = isPreliminary
    ? formatPreliminary({
        preliminaryPrice,
        decimals,
        lastPrice: meta.last_value ?? 0,
        price: preliminaryPrice.close,
        dateLocale,
      })
    : officialCloseDetails;

  const primaryPrice = {
    ...base,
    isLive,
    ...customPrimaryDetails,
  };

  const outsideHours = getOutsideHoursText({
    marketHours: base.marketHours,
    preMarketLabel,
    afterHoursLabel,
  });
  const customSecondaryDetails = isPreliminary
    ? formatPreliminary({
        preliminaryPrice,
        decimals,
        lastPrice: preliminaryPrice.close,
        price: base.price ?? 0,
        dateLocale,
      })
    : base;

  const secondaryPrice = {
    ...base,
    outsideHours,
    ...customSecondaryDetails,
    isLive: !base.marketHours.isMarketHours && base?.isLive,
  };

  return { primaryPrice, secondaryPrice };
};

export const isNonZeroReturn = (value?: number | string): boolean =>
  parseFloat(String(value ?? '0')) !== 0;

export const getDisplayPriceChange = <T extends DisplayPriceChange>(
  priceChange?: T,
  isLive?: boolean
): T | undefined => {
  if (!isLive) {
    return undefined;
  }

  if (!isNonZeroReturn(priceChange?.proportionChange)) {
    return undefined;
  }

  return priceChange;
};

export const checkSupportedExchange = (exchange: string) => {
  return (SUPPORTED_EXCHANGES as readonly string[]).includes(exchange);
};

export const getOutsideHoursText = ({
  marketHours,
  preMarketLabel,
  afterHoursLabel,
}: GetOutsideHoursTextProps): OutsideHoursText | undefined => {
  if (marketHours.isPreMarket) {
    return { label: preMarketLabel, tag: 'pre' };
  }

  if (marketHours.isAfterMarket) {
    return { label: afterHoursLabel, tag: 'after' };
  }

  return undefined;
};

export const getMarketHours = (date?: Date): MarketHours => {
  const estZoned = toZonedTime(date ?? new Date(), EST_TIME_ZONE);

  if (isWeekend(estZoned)) {
    return {
      isPreMarket: false,
      isAfterMarket: false,
      isMarketHours: false,
    };
  }

  const dayStart = parse('00:00:00', 'HH:mm:ss', estZoned);
  const coreTradingStart = parse('9:30:00', 'HH:mm:ss', estZoned);
  const coreTradingEnd = parse('16:00:00', 'HH:mm:ss', estZoned);
  const dayEnd = parse('23:59:59', 'HH:mm:ss', estZoned);

  const isPreMarket = isWithinInterval(estZoned, {
    start: dayStart,
    end: coreTradingStart,
  });
  const isMarketHours = isWithinInterval(estZoned, {
    start: coreTradingStart,
    end: coreTradingEnd,
  });
  const isAfterMarket = isWithinInterval(estZoned, {
    start: coreTradingEnd,
    end: dayEnd,
  });

  return {
    isPreMarket,
    isAfterMarket,
    isMarketHours,
  };
};

export const checkIsPreliminary = (
  alifiedLastTimestamp: string,
  preliminaryTime: string
) => {
  const snakeTimeZoned = toZonedTime(new Date(alifiedLastTimestamp), 'UTC');
  const preliminaryTimeZoned = toZonedTime(
    new Date(preliminaryTime),
    EST_TIME_ZONE
  );
  const snakeTimeStart = parse('00:00:00', 'HH:mm:ss', snakeTimeZoned);
  const preliminaryTimeStart = parse(
    '00:00:00',
    'HH:mm:ss',
    preliminaryTimeZoned
  );

  return (
    getTimeMinusOffset(snakeTimeStart) <
    getTimeMinusOffset(preliminaryTimeStart)
  );
};

export const formatPreliminaryDate = ({
  preliminaryTime,
  dateLocale,
}: FormatPreliminaryDateProps) => {
  const navigatorLanguage =
    typeof window !== 'undefined' ? window.navigator.language : undefined;
  const language = (dateLocale ?? navigatorLanguage ?? 'en')
    .split('-')[0]
    .toUpperCase();
  const dateFormats = Formats['MMMM do, k:mm zzz'];
  const format =
    dateFormats[language as keyof LocalisedDateFormatting] ||
    dateFormats.default;

  return formatInTimeZone(preliminaryTime, EST_TIME_ZONE, format);
};
